/**
 * Main navigation component.
 *
 * @Author: Jon Lucas
 * @Date:   2017-09-11T16:29:08+02:00
 * @Email:  jon.lucas@mazedia.fr
 * @Last modified by:   Jon Lucas
 * @Last modified time: 2017-09-29T16:13:27+02:00
 */
import { Component, Input, ViewChild, ElementRef, QueryList, ViewChildren, AfterViewInit } from "@angular/core";
import { Router } from "@angular/router";
import { DomSanitizer, SafeStyle } from "@angular/platform-browser";
import { AbstractComponent } from "../../abstract-component/abstract.component";
import { BaseDataType } from "../../base-data-type";
import { ServiceLocator } from "../../service-locator";
import { AppService } from "../../app.service";
import "rxjs/add/operator/filter";
import { Utils } from "../../util/Utils";
import { environment } from "../../../environments/environment";
import { LoaderUtils } from "../../util/LoaderUtils";
import { ApiUtils } from "../../util/ApiUtils";


@Component({
    selector: "app-main-navigation",
    templateUrl: "./main-navigation.component.html",
    styleUrls: ["./main-navigation.component.scss"]
})

export class MainNavigationComponent extends AbstractComponent implements AfterViewInit {

    public readonly TAG: string = "[MainNavigationComponent]";

    protected router: Router;

    @Input() public lang: string;

    @ViewChild("mainNavigation") mainNavigation: ElementRef;
    @ViewChildren("subtypes") subtypes: QueryList<ElementRef>;

    public types: BaseDataType[];
    public selectedType: BaseDataType;
    public selectedSubType: BaseDataType;

    private selectedTypeIndex: number;

    public landmarks: { [key: string]: BaseDataType };

    private _typeId: string;
    private _subtypeId: string;

    // Store requested subsection's values here
    private configDictionary: { [key: string]: BaseDataType[] } = {};

    private optionDictionary: { [key: string]: BaseDataType } = {};

    public hasContactForm: boolean = true;

    constructor(
        private sanitizer: DomSanitizer,
        private apiUtils: ApiUtils,
        private el: ElementRef
    ) {
        super();

        this.router = ServiceLocator.injector.get(Router);
    }

    /**
     * Check if all conditions are filled, to show navigation element.
     *
     * @see {@link README.md#Conditions} for further information.
     * @param pType {BaseDataType}
     */
    private conditionsValidated(pType: BaseDataType): boolean {
        return Utils.conditionsValidated(pType);
    }

    private handleError(error: any): void {
        console.error(this.TAG, "An error occurred", error);
    }

    public get typeId(): string {
        return this._typeId;
    }

    @Input()
    public set typeId(pId: string) {
        this._typeId = pId;

        if (this.types && (this.selectedType === undefined || this.selectedType.id !== this.typeId)) {
            this.selectedTypeIndex = this.types.findIndex(type => type.id === this.typeId);
            this.selectedType = this.types[this.selectedTypeIndex];
        }
    }

    public get subtypeId(): string {
        return this._subtypeId;
    }

    @Input()
    public set subtypeId(pId: string) {
        this._subtypeId = pId;

        if (this.selectedType && this.subtypeId && (this.selectedSubType === undefined || this.selectedSubType.id !== this.subtypeId)) {
            this.selectedSubType = this.selectedType.values.find(subtype => subtype.id === this.subtypeId);
        }
        else {
            this.selectedSubType = undefined;
        }
    }

    /**
     * Main section selected
     *
     * @param type {BaseDataType}
     */
    public onSelect(type: BaseDataType): void {
        this.router.navigate(["/lang", (this.lang ? this.lang : 'fr'), type.id, ""]);
    }

    /**
     * Subsection selected
     *
     * @param type {BaseDataType}
     * @param subtype {BaseDataType}
     * @param event {Event}
     */
    public onSelectSub(type: BaseDataType, subtype: BaseDataType, event: Event = null): void {
        event.stopPropagation();

        if (subtype.id === "save-configuration-type") {
            this.onSaveConfiguration();
        } else {
            this.router.navigate(["/lang", (this.lang ? this.lang : 'fr'), type.id, subtype.id]);
        }
    }

    private onSaveConfiguration(): void {
        let commands: string[];
        let values: any[] = this.confDataService.getElementValues("roof-dimensions-valid");
        if (!values || values.length === 0 || values[0] !== "true") {
            // Roof dimensions form invalid
            commands = ["/lang", this.lang, "building", "roof-dimensions"];
            this.router.navigate(commands);
            return;
        }
        values = this.confDataService.getElementValues("surelevation-type");
        if (values && values.length > 0 && values[0] !== "no-surelevation") {
            values = this.confDataService.getElementValues("surelevation-dimensions-valid");
            if (!values || values.length === 0 || values[0] !== "true") {
                // Surelevation dimensions form invalid
                commands = ["/lang", this.lang, "building", "surelevation-dimensions"];
                this.router.navigate(commands);
                return;
            }
        }
        // Check if belriv dimensions valid
        values = this.confDataService.getElementValues("project-type");
        if (values && values.length > 0 && values[0] !== "projet-goutiere") {
            // Check belriv choose
            values = this.confDataService.getElementValues("belriv-type");
            if (!values || values.length === 0) {
                // Belriv type form invalid
                commands = ["/lang", this.lang, "project", "belriv-type"];
                this.router.navigate(commands);
                return;
            }

            values = this.confDataService.getElementValues("belriv-dimensions-valid");
            if (!values || values.length === 0 || values[0] !== "true") {
                // Belriv dimensions form invalid
                commands = ["/lang", this.lang, "installation-and-accessories", "belriv-dimensions"];
                this.router.navigate(commands);
                return;
            }

            // Check profile muraux
            values = this.confDataService.getElementValues("wall-profile-type");
            if (!values || values.length === 0) {
                // Belriv type form invalid
                commands = ["/lang", this.lang, "installation-and-accessories", "wall-profile-type"];
                this.router.navigate(commands);
                return;
            }

            // Check installation-type == pose perpendiculaire || pose incliné
            values = this.confDataService.getElementValues("installation-type");
            if (!values || values.length === 0 || (values[0] !== "pose_horizontale" && values[0] !== "pose_inclinee")) {
                // Belriv type form invalid
                commands = ["/lang", this.lang, "installation-and-accessories", "installation-type"];
                this.router.navigate(commands);
                return;
            }
        }

        // Check if gouttiere dimensions valid
        values = this.confDataService.getElementValues("project-type");
        if (values && values.length > 0 && values[0] !== "projet-debord") {
            // Check profile choose
            values = this.confDataService.getElementValues("profile-type");
            if (!values || values.length === 0) {
                // profile type form invalid
                commands = ["/lang", this.lang, "project", "profile-type"];
                this.router.navigate(commands);
                return;
            }
            // Check descente choose
            values = this.confDataService.getElementValues("descente-type");
            if (!values || values.length === 0) {
                // descente type form invalid
                commands = ["/lang", this.lang, "project", "descente-type"];
                this.router.navigate(commands);
                return;
            }
        }

        values = this.confDataService.getElementValues("project-type");
        if (values && values.length > 0 && values[0] === "projet-goutiere") {
            // Check installation-type == pose perpendiculaire || pose incliné
            values = this.confDataService.getElementValues("installation-type");
            if (!values || values.length === 0 || values[0] === "pose_horizontale" || values[0] === "pose_inclinee") {
                // Belriv type form invalid
                commands = ["/lang", this.lang, "installation-and-accessories", "installation-type"];
                this.router.navigate(commands);
                return;
            }

            //crochet-type
            values = this.confDataService.getElementValues("crochet-type");
            const options = this.confDataService.getElementOptions("crochet-type");
            let valuesInstallation = this.confDataService.getElementValues("installation-type");
            if ((!values || values.length === 0) && valuesInstallation[0] !== "pose_sur_bandeau") {
                // Belriv type form invalid
                commands = ["/lang", this.lang, "installation-and-accessories", "crochet-type"];
                this.router.navigate(commands);
                return;
            } else if (values && values.hasOwnProperty(0) && values[0] === "hampe_galvanisee" && options === undefined) {
                // Belriv type form invalid
                commands = ["/lang", this.lang, "installation-and-accessories", "crochet-type"];
                this.router.navigate(commands);
                return;
            }
        }

        if (!this.hasContactForm) {
            this.confDataService.removeValue("contact-form");
        }

        LoaderUtils.addLoader("contact-form-loader", <HTMLElement>document.getElementsByTagName('app-main-view')[0], false);
        LoaderUtils.showLoader("contact-form-loader");

        this.apiUtils.saveDevis();
    }

    public getData(): void {
        super.getData();

        this.appService.getData(AppService.THREED_VIEW_LANDMARKS_URL)
            .then(
                types => {
                    this.landmarks = {};
                    for (const type of types) {
                        this.landmarks[type.id] = type;
                    }

                    this.getDataNavigationMenu();
                }
            );


    }

    private getDataNavigationMenu() {
        this.appService.getData(AppService.NAVIGATION_MENU_URL).then(
            types => {
                this.types = types;

                // Force values update
                for (const type of this.types) {
                    if (type.values === undefined) {
                        continue;
                    }
                    for (const subtype of type.values) {
                        this.getCurrentTypeValues(subtype.id);
                        if (this.conditionsValidated(subtype)) {
                            this.typeId = type.id;
                            this.subtypeId = subtype.id;
                        }
                    }
                }

                /**
                 * Add a section or a subsection here to not navigate to last subsection.
                 * This avoids to select a subsection displaying in the submenu.
                 */
                let navigateToSubtype: boolean = true;
                switch (this.typeId) {
                    case "building-customization":
                    case "project":
                    case "project-type-choice":
                        navigateToSubtype = false;
                        break;

                    default:

                        switch (this.subtypeId) {
                            case "roof-type":
                            case "surelevation-type":
                                navigateToSubtype = false;
                                break;
                            default:
                                break;
                        }

                        break;
                }

                // Navigate to last conditions valid subsection.
                // Exception made for above sections and subsections to not show submenu at start.
                this.router.navigate(["/lang", (this.lang ? this.lang : 'fr'), this.typeId, navigateToSubtype ? this.subtypeId : ""]);
            }
        );
    }

    /**
     * Get option's selected color value.
     *
     * @param pId {string}
     */
    private selectedColor(pId: string): SafeStyle {

        const value: string = this._getOptionValue(pId);

        return value && value.indexOf("#") === 0 ? this.sanitizer.bypassSecurityTrustStyle(value) : undefined;
    }

    /**
     * Get option's selected url value.
     *
     * @param pId {string}
     */
    private selectedUrl(pId: string): SafeStyle {

        const value: string = this._getOptionValue(pId);

        return value && value.indexOf("#") === -1 ? this._detectTypeAndSanitizeUrl(value) : undefined;
    }

    /**
     * Return true if the sub section is validated
     *
     * @param pId {string}
     */
    protected isSubTypeActivated(subTypeId: string): boolean {

        switch (subTypeId) {
            case "project-type": {
                const valid = this.confDataService.getElementValues("project-type-valid");
                return valid && valid[0] === "true";
            }
            case "roof-dimensions": {
                const valid = this.confDataService.getElementValues("roof-dimensions-valid");
                return valid && valid[0] === "true";
            }
            case "surelevation-dimensions": {
                const valid = this.confDataService.getElementValues("surelevation-dimensions-valid");
                return valid && valid[0] === "true";
            }
            case "accessories-type": {
                const valid = this.confDataService.getElementValues("accessories-type-valid");
                return valid && valid[0] === "true";
            }
            case "contact-form": {
                const valid = this.confDataService.getElementValues("contact-form-valid");
                return valid && valid[0] === "true";
            }
            default: {
                return this.configDictionary[subTypeId] !== undefined;
            }
        }

    }

    /**
     * Return true if the section is validated
     *
     * @param pId {string}
     */
    protected isTypeActivated(type: BaseDataType, index: number): boolean {
        let conditionValidatedCounter = 0;
        for (const sub of type.values) {
            const conditionsValidated = this.conditionsValidated(sub);
            if (conditionsValidated) {
                conditionValidatedCounter++;
                if (!this.isSubTypeActivated(sub.id)) {
                    // console.log(this.TAG, "Subtype '" + sub.id + "' prevents type '" + type.id + "' to be activated !");
                    return false;
                }
            }
        }

        if (conditionValidatedCounter <= 0) {
            return false;
        }

        return true;

    }

    /**
     * Get configuration element's selected option value.
     *
     * @param pId {string} Confdata element's id.
     */
    private _getOptionValue(pId: string): string {
        if (this.configDictionary[pId] === undefined) {
            this.getCurrentTypeValues(pId);
            return;
        }

        if (this.configDictionary[pId][0].options === undefined || this.configDictionary[pId][0].options[0].values === undefined) {
            return;
        }

        const option: any = this.configDictionary[pId][0].options[0].values.find(opt => opt.id === this.confDataService.getElementOptions(pId)[0]);

        if (!option || !option.values || option.values.length === 0) {
            return;
        }

        return option.values[0];
    }

    /**
     * Detects url type by its extension.
     * If it is an image, show it on the button.
     *
     * @param pValue {string}
     */
    private _detectTypeAndSanitizeUrl(pValue: string): SafeStyle {
        const extension: string = pValue.split(".").pop().toLowerCase();
        if (extension === "jpg" || extension === "jpeg" || extension === "png" || extension === "gif") {
            return this.sanitizer.bypassSecurityTrustStyle(`url("${pValue}")`);
        }
        return;
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.selectedTypeIndex = 0;

        // Listen to configuration update events
        this.confDataService.observable.subscribe(
            value => {
                this.getCurrentTypeValues(value.id);
            }
        );

        this.hasContactForm = environment.has_contact_form;
    }

    public ngAfterViewInit(): void {
        this.subtypes.changes.subscribe(list => {
            const element: HTMLLIElement = (<HTMLLIElement>list.last.nativeElement);
            Utils.scrollIntoView(element, this.mainNavigation.nativeElement);
        });
    }

    /**
     * Get current subtypes' selected values and options.
     *
     * @param pId {string} BaseDataType's id value
     */
    private getCurrentTypeValues(pId: string): void {
        console.log(this.TAG, "confdataservice update", pId);

        if (this.confDataService.confdata[pId] === undefined) {
            return;
        }

        if (!this.confDataService.getElementValues(pId) || this.confDataService.getElementValues(pId).length === 0) {
            this.configDictionary[pId] = undefined;
            return;
        }

        const values: any = this.confDataService.getElementValues(pId);

        // Exception for forms datas like roof-dimensions.
        // We store a basedatatype json object, not just a string id.
        if (typeof values[0] !== "string") {
            this.configDictionary[pId] = this.confDataService.getElementValues(pId);
            return;
        }

        if (this.confDataService.getApiUrl(pId) === undefined) {
            return;
        }

        // We get the current subsections selected values' basedatatype, to show them.
        this.appService.getData(this.confDataService.getApiUrl(pId)).then(
            (types: BaseDataType[]) => {
                this.configDictionary[pId] = types.filter(type => values.indexOf(type.id) !== -1);
            }
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/main-left-column/main-navigation/main-navigation.component.ts